'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Area, BarChart, Bar, LineChart, Line, Funnel, PieChart, Pie, Cell, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,  } from 'recharts';

type TimeRange = '7d' | '30d' | '90d';

const revenueData: Record<TimeRange, Array<{ period: string; revenue: number; target: number; profit: number; refunds: number }>> = {
  '7d': [
    { period: 'Mon', revenue: 9800, target: 9000, profit: 3200, refunds: 420 },
    { period: 'Tue', revenue: 8600, target: 9000, profit: 2800, refunds: 310 },
    { period: 'Wed', revenue: 12400, target: 9000, profit: 4100, refunds: 280 },
    { period: 'Thu', revenue: 14200, target: 9000, profit: 4700, refunds: 190 },
    { period: 'Fri', revenue: 17100, target: 9000, profit: 5600, refunds: 340 },
    { period: 'Sat', revenue: 20800, target: 9000, profit: 6900, refunds: 510 },
    { period: 'Sun', revenue: 15600, target: 9000, profit: 5100, refunds: 390 },
  ],
  '30d': [
    { period: 'Wk 1', revenue: 72400, target: 65000, profit: 23800, refunds: 2100 },
    { period: 'Wk 2', revenue: 66200, target: 65000, profit: 21700, refunds: 1800 },
    { period: 'Wk 3', revenue: 79800, target: 65000, profit: 26200, refunds: 2400 },
    { period: 'Wk 4', revenue: 70100, target: 65000, profit: 23000, refunds: 1950 },
  ],
  '90d': [
    { period: 'Jan', revenue: 258000, target: 240000, profit: 84600, refunds: 7200 },
    { period: 'Feb', revenue: 226000, target: 240000, profit: 74100, refunds: 6400 },
    { period: 'Mar', revenue: 284500, target: 240000, profit: 93300, refunds: 8100 },
  ],
};

const funnelStages = [
  { stage: 'Website Visitors', count: 45200, dropOff: 0, color: '#0066CC' },
  { stage: 'Search Initiated', count: 28400, dropOff: 37.2, color: '#0052A3' },
  { stage: 'Results Viewed', count: 18400, dropOff: 35.2, color: '#3B82F6' },
  { stage: 'Flight Selected', count: 8200, dropOff: 55.4, color: '#FF6B35' },
  { stage: 'Booking Started', count: 3560, dropOff: 56.6, color: '#F59E0B' },
  { stage: 'Payment Page', count: 1890, dropOff: 46.9, color: '#8B5CF6' },
  { stage: 'Booking Confirmed', count: 1247, dropOff: 34.0, color: '#10B981' },
];

const userBehaviourData = [
  { page: 'Homepage', sessions: 45200, avgTime: '2:34', bounceRate: 32, conversions: 1247 },
  { page: 'Flight Search', sessions: 28400, avgTime: '5:12', bounceRate: 18, conversions: 8200 },
  { page: 'Flight Results', sessions: 18400, avgTime: '7:45', bounceRate: 22, conversions: 3560 },
  { page: 'Booking Form', sessions: 3560, avgTime: '9:20', bounceRate: 47, conversions: 1890 },
  { page: 'Confirmation', sessions: 1247, avgTime: '1:45', bounceRate: 5, conversions: 1247 },
];

const deviceData = [
  { name: 'Mobile', value: 58, color: '#0066CC' },
  { name: 'Desktop', value: 32, color: '#10B981' },
  { name: 'Tablet', value: 10, color: '#FF6B35' },
];

const sessionDurationData = [
  { duration: '0–30s', users: 4200 },
  { duration: '30s–2m', users: 8900 },
  { duration: '2–5m', users: 14200 },
  { duration: '5–10m', users: 11800 },
  { duration: '10–20m', users: 4300 },
  { duration: '20m+', users: 1800 },
];

const cohortData = [
  { month: 'Jan', week1: 100, week2: 68, week3: 52, week4: 44 },
  { month: 'Feb', week1: 100, week2: 71, week3: 55, week4: 47 },
  { month: 'Mar', week1: 100, week2: 74, week3: 58, week4: 50 },
];

const revenueByChannelData = [
  { channel: 'Organic', revenue: 112400, bookings: 487, cac: 0 },
  { channel: 'Direct', revenue: 78200, bookings: 342, cac: 0 },
  { channel: 'Paid Ads', revenue: 52800, bookings: 228, cac: 48 },
  { channel: 'Social', revenue: 28400, bookings: 124, cac: 32 },
  { channel: 'Referral', revenue: 12700, bookings: 66, cac: 18 },
];

const EnhancedAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [activeSection, setActiveSection] = useState<'revenue' | 'funnel' | 'behaviour' | 'cohort'>('revenue');

  const sections = [
    { id: 'revenue' as const, label: 'Revenue Trends', icon: 'CurrencyPoundIcon' },
    { id: 'funnel' as const, label: 'Conversion Funnel', icon: 'FunnelIcon' },
    { id: 'behaviour' as const, label: 'User Behaviour', icon: 'CursorArrowRaysIcon' },
    { id: 'cohort' as const, label: 'Cohort Analysis', icon: 'TableCellsIcon' },
  ];

  const data = revenueData[timeRange];
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const totalProfit = data.reduce((s, d) => s + d.profit, 0);
  const totalRefunds = data.reduce((s, d) => s + d.refunds, 0);
  const profitMargin = ((totalProfit / totalRevenue) * 100).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-black text-foreground">Enhanced Analytics</h2>
          <p className="text-sm text-muted-foreground mt-1">Deep insights — conversion funnels, revenue trends &amp; user behaviour</p>
        </div>
        <div className="flex gap-1">
          {(['7d', '30d', '90d'] as TimeRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setTimeRange(r)}
              className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                timeRange === r ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-muted'
              }`}
            >
              {r === '7d' ? '7D' : r === '30d' ? '30D' : '90D'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue', value: `£${(totalRevenue / 1000).toFixed(1)}k`, icon: 'CurrencyPoundIcon', color: 'text-green-600', bg: 'bg-green-50', change: '+18.2%' },
          { label: 'Gross Profit', value: `£${(totalProfit / 1000).toFixed(1)}k`, icon: 'ArrowTrendingUpIcon', color: 'text-blue-600', bg: 'bg-blue-50', change: `${profitMargin}% margin` },
          { label: 'Refunds', value: `£${(totalRefunds / 1000).toFixed(1)}k`, icon: 'ArrowUturnLeftIcon', color: 'text-red-600', bg: 'bg-red-50', change: '-2.1%' },
          { label: 'Funnel Conv.', value: '2.76%', icon: 'FunnelIcon', color: 'text-purple-600', bg: 'bg-purple-50', change: '+0.4%' },
        ].map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-border">
            <div className={`w-10 h-10 ${kpi.bg} rounded-lg flex items-center justify-center mb-3`}>
              <Icon name={kpi.icon as any} size={20} className={kpi.color} />
            </div>
            <div className="text-xl font-black text-foreground">{kpi.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5 mb-1">{kpi.label}</div>
            <span className="text-xs font-bold text-green-600">{kpi.change}</span>
          </div>
        ))}
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              activeSection === s.id ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={s.icon as any} size={16} />
            {s.label}
          </button>
        ))}
      </div>

      {/* Revenue Trends */}
      {activeSection === 'revenue' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-5">Revenue vs Target vs Profit</h3>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number, n: string) => [`£${v.toLocaleString()}`, n]} />
                <Legend />
                <Area type="monotone" dataKey="revenue" fill="url(#revGrad)" stroke="#10B981" strokeWidth={2} name="Revenue" />
                <Line type="monotone" dataKey="target" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={false} />
                <Bar dataKey="profit" fill="#0066CC" opacity={0.8} name="Profit" radius={[3, 3, 0, 0]} />
                <Bar dataKey="refunds" fill="#EF4444" opacity={0.7} name="Refunds" radius={[3, 3, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-5">Revenue by Acquisition Channel</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenueByChannelData} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="channel" tick={{ fontSize: 11 }} width={60} />
                <Tooltip formatter={(v: number) => [`£${v.toLocaleString()}`, 'Revenue']} />
                <Bar dataKey="revenue" fill="#0066CC" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="bookings" position="right" formatter={(v: number) => `${v} bkgs`} style={{ fontSize: 10, fill: '#6B7280' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Conversion Funnel */}
      {activeSection === 'funnel' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-2">Full Booking Conversion Funnel</h3>
            <p className="text-sm text-muted-foreground mb-6">Track where users drop off in the booking journey</p>
            <div className="space-y-3">
              {funnelStages.map((stage, i) => {
                const pct = Math.round((stage.count / funnelStages[0].count) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-surface text-xs font-black text-muted-foreground flex items-center justify-center">{i + 1}</span>
                        <span className="text-sm font-bold text-foreground">{stage.stage}</span>
                        {stage.dropOff > 0 && (
                          <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full font-bold">-{stage.dropOff}% drop</span>
                        )}
                      </div>
                      <span className="text-sm font-black text-foreground">{stage.count.toLocaleString()}</span>
                    </div>
                    <div className="h-9 bg-gray-100 rounded-lg overflow-hidden">
                      <div
                        className="h-full rounded-lg flex items-center px-3 transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: stage.color }}
                      >
                        <span className="text-white text-xs font-bold">{pct}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="bg-green-50 rounded-xl p-3 text-center border border-green-200">
                <div className="text-lg font-black text-green-700">2.76%</div>
                <div className="text-xs text-green-600">Visitor → Booking</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-200">
                <div className="text-lg font-black text-blue-700">35.0%</div>
                <div className="text-xs text-blue-600">Search → Selection</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-3 text-center border border-orange-200">
                <div className="text-lg font-black text-orange-700">66.0%</div>
                <div className="text-xs text-orange-600">Payment → Confirm</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-4">Biggest Drop-off Points</h3>
            <div className="space-y-3">
              {funnelStages.filter((s) => s.dropOff > 40).sort((a, b) => b.dropOff - a.dropOff).map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-3">
                    <Icon name="ExclamationTriangleIcon" size={18} className="text-red-500" />
                    <div>
                      <div className="text-sm font-bold text-foreground">{s.stage}</div>
                      <div className="text-xs text-muted-foreground">{s.count.toLocaleString()} users reach this stage</div>
                    </div>
                  </div>
                  <span className="text-sm font-black text-red-600">-{s.dropOff}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* User Behaviour */}
      {activeSection === 'behaviour' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-border p-5">
              <h3 className="text-lg font-heading font-black text-foreground mb-4">Session Duration Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={sessionDurationData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="duration" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => [v.toLocaleString(), 'Users']} />
                  <Bar dataKey="users" fill="#0066CC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl border border-border p-5">
              <h3 className="text-lg font-heading font-black text-foreground mb-4">Device Breakdown</h3>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={deviceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {deviceData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v}%`, 'Share']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {deviceData.map((d, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-sm text-muted-foreground">{d.name}</span>
                      </div>
                      <span className="text-sm font-black text-foreground">{d.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="p-5 border-b border-border">
              <h3 className="text-lg font-heading font-black text-foreground">Page Performance &amp; User Behaviour</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface">
                  <tr>
                    {['Page', 'Sessions', 'Avg Time', 'Bounce Rate', 'Conversions', 'Conv. Rate'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userBehaviourData.map((row, i) => (
                    <tr key={i} className="border-t border-border hover:bg-surface transition-colors">
                      <td className="px-4 py-3 font-bold text-foreground text-sm">{row.page}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{row.sessions.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{row.avgTime}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          row.bounceRate < 25 ? 'bg-green-100 text-green-700' : row.bounceRate < 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }`}>{row.bounceRate}%</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-primary">{row.conversions.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600">
                        {((row.conversions / row.sessions) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Cohort Analysis */}
      {activeSection === 'cohort' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-2">User Retention Cohort</h3>
            <p className="text-sm text-muted-foreground mb-6">Percentage of users returning each week after first visit</p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface">
                    <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase">Cohort</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-muted-foreground uppercase">Week 1</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-muted-foreground uppercase">Week 2</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-muted-foreground uppercase">Week 3</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-muted-foreground uppercase">Week 4</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortData.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-3 font-bold text-foreground text-sm">{row.month} Cohort</td>
                      {[row.week1, row.week2, row.week3, row.week4].map((val, j) => (
                        <td key={j} className="px-4 py-3 text-center">
                          <div
                            className="inline-flex items-center justify-center w-14 h-8 rounded-lg text-xs font-black text-white"
                            style={{ backgroundColor: `rgba(0, 102, 204, ${val / 100})` }}
                          >
                            {val}%
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-lg font-heading font-black text-foreground mb-4">Weekly Retention Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={cohortData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <Tooltip formatter={(v: number) => [`${v}%`, 'Retention']} />
                <Legend />
                <Line type="monotone" dataKey="week2" stroke="#0066CC" strokeWidth={2} name="Week 2" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="week3" stroke="#FF6B35" strokeWidth={2} name="Week 3" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="week4" stroke="#10B981" strokeWidth={2} name="Week 4" dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAnalyticsDashboard;
